import { css, cx, useTheme } from '@fable/theme'
import { StreakCalendarGridItem as GridItemType } from './types'
import { Text } from '@fable/components'
import { StreakStat } from '../../../types'
import { useStats } from '../../../lib/ctx/stats'
import { StreakCalendarBooks } from './StreakCalendarBooks'
import { StreakCalendarCellBg } from './StreakCalendarCellBg'

export interface StreakCalendarCellProps extends GridItemType {
  streak?: StreakStat | null
  isBest?: boolean
  animate?: boolean
}

export const useStreakCellTheme = ({
  streak,
  isBest,
  isCurrentMonth,
}: {
  streak?: StreakStat | null
  isBest: boolean
  isCurrentMonth: boolean
}) => {
  const { colors } = useTheme()
  const theme = useStats((s) => s.theme)

  if (streak) {
    if (isBest) {
      return {
        bg: theme.primary,
        text: `${colors.uiGreenUltraDark}80`,
      }
    }

    return {
      bg: theme.streakCalendar?.daysReadBg || colors.uiWhite20,
      text: `${colors.uiGreenUltraDark}80`,
    }
  }

  if (isCurrentMonth) {
    return {
      bg: 'transparent',
      text: colors.uiWhite20,
    }
  }

  return {
    border: colors.uiBlack40,
    bg: 'transparent',
    text: colors.uiBlack60,
  }
}

export const StreakCalendarCell = ({
  streak,
  isBest = false,
  day,
  isCurrentMonth,
  animate = false,
}: StreakCalendarCellProps) => {
  const { colors } = useTheme()
  const theme = useStats((s) => s.theme)
  const { border, bg, text } = useStreakCellTheme({
    streak,
    isBest,
    isCurrentMonth,
  })

  const getBg = () => {
    if (isCurrentMonth) {
      return theme.streakCalendar?.bg || colors.uiBlack40
    }
    return 'transparent'
  }

  return (
    <div
      data-testid={
        streak
          ? `streak-calendar-cell-${isBest ? 'best' : 'read'}`
          : 'streak-calendar-cell'
      }
      className={cx(
        css`
          padding: 4px 5px;
          border-radius: 10px;
          background-color: ${getBg()};
          width: 100%;
          height: 100%;
          overflow: hidden;
          position: relative;
        `,
        border?.length
          ? css`
              padding: 4px 5px;
              border: 2px solid ${border};
            `
          : css`
              padding: 6px 7px;
            `
      )}
    >
      <StreakCalendarCellBg animate={animate} color={bg} />
      <Text
        className={css`
          cursor: default;
          position: relative;
          z-index: 1;
        `}
        type="label"
        sizing="M"
        weight="500"
        color={text}
      >
        {day?.toString()}
      </Text>
      {streak ? (
        <StreakCalendarBooks streak={streak} animate={animate} />
      ) : null}
    </div>
  )
}
